/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HttpErrorResponse } from '@angular/common/http';
import type { IClaimStatusRouting } from 'src/app/shared/interfaces/claim-status/claim-status.interface';
import type { IEligibilityRouting } from 'src/app/shared/interfaces/eligibility-routing/eligibility-routing.interface';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { type Observable, Subscription } from 'rxjs';
import {
  IGQLPayersParams,
  IPayer,
} from 'src/app/shared/interfaces/payer/payers.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ClaimStatusRoutingService } from 'src/app/shared/services/claims-status-routing/claims-status-routing.service';
import { EligibilityRoutingService } from 'src/app/shared/services/eligibility-routing/eligibility-routing.service';
import { PayerGQLService } from 'src/app/shared/services/payers/payers.graphql.service';

@Component({
  selector: 'app-edit-claim-status-routing-modal',
  templateUrl: './edit-claim-status-routing-modal.component.html',
})
export class EditClaimStatusRoutingModalComponent implements OnInit, OnDestroy {
  @Input() claimStatusRouting;
  @Input() isClaimsEdit = false;

  claimStatusRoutingForm: UntypedFormGroup;

  isEditMode = false;
  isLoading = true;
  isPayerLoading = true;

  payers: IPayer[] = [];
  nextCursor: string;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    private claimStatusRoutingService: ClaimStatusRoutingService,
    private eligibilityRoutingService: EligibilityRoutingService,
    private payerGQLService: PayerGQLService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isEditMode = !!this.claimStatusRouting;
    this.loadPayers();

    this.claimStatusRoutingForm = this.fb.group({
      payerName: [this.claimStatusRouting?.payerName || null],
      id: [this.claimStatusRouting?.id || '', [Validators.required]],
      ediId: [this.claimStatusRouting?.ediId || ''],
      groupId: [this.claimStatusRouting?.groupId || ''],
      onlineBatch: [this.claimStatusRouting?.onlineBatch || ''],
      processorId: [this.claimStatusRouting?.processorId || ''],
      scId: [
        {
          value: this.claimStatusRouting?.scId || '',
          disabled: this.isEditMode,
        },
        [Validators.required],
      ],
      recordStatus: [this.claimStatusRouting?.recordStatus || 'A'],
    });
  }

  close() {
    this.activeModal.dismiss();
  }

  save() {
    this.isLoading = true;

    const formValue = { ...this.claimStatusRoutingForm.getRawValue() };

    let request$;

    if (this.isClaimsEdit) {
      request$ = this.isEditMode
        ? this.claimStatusRoutingService.editClaimStatusRouting(
            formValue as IClaimStatusRouting,
          )
        : this.claimStatusRoutingService.createClaimStatusRouting(
            formValue as IClaimStatusRouting,
          );
    } else {
      request$ = this.isEditMode
        ? this.eligibilityRoutingService.editEligibilityRouting(
            formValue as IEligibilityRouting,
          )
        : this.eligibilityRoutingService.createEligibilityRouting(
            formValue as IEligibilityRouting,
          );
    }

    this.subscriptions.add(
      (request$ as Observable<void>).subscribe({
        next: () => {
          const element = this.isClaimsEdit
            ? 'Claim Status Routing'
            : 'Eligibility Routing';

          const successMessage = this.isEditMode
            ? `${element} was updated successfully!`
            : `${element} was created successfully!`;

          this.alertService.success(successMessage);
          this.isLoading = false;
          this.activeModal.close();
        },
        error: (error: unknown) => {
          console.log(error);
          this.alertService.error(error as HttpErrorResponse | string);
          this.isLoading = false;
        },
      }),
    );
  }

  onScrollToEnd() {
    this.loadPayers();
  }

  onSearch($event: any) {
    setTimeout(() => {
      if ($event.term.length > 0 && $event.items.length === 0) {
        this.loadPayers($event.term as string);
      }
    }, 150);
  }

  customSearchFn(term: string, item: IPayer) {
    term = term.toLocaleLowerCase();
    return (
      item.id.toLocaleLowerCase().includes(term) ||
      item.name.toLocaleLowerCase().includes(term)
    );
  }

  private loadPayers(payerName?: string) {
    const modefiedParams = {
      filter: {
        name: payerName ?? null,
      },
      after: this.nextCursor || null,
    } as IGQLPayersParams;

    this.subscriptions.add(
      this.payerGQLService.getPayers(modefiedParams).subscribe({
        next: (response) => {
          this.isPayerLoading = false;
          this.nextCursor = response.pageInfo.endCursor;
          this.payers = this.payers.concat(response.nodes);
        },
        error: (error) => {
          this.isPayerLoading = false;
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
        },
      }),
    );
  }
}
